import { useMemo } from 'react';
import { getTenantDashboardOverview } from '@/api/adminDashboardService';
import { createApiCall, useDashboardData } from '@/hooks/useDashboardData';
import type { RawMeeting } from '@/pages/MeetingDashboard/MeetingDashboard';

const normalizePitch = (pitch: any): RawMeeting => {
  const normalized: any = {
    ...pitch,
    id:                pitch.id,
    meetingType:       pitch.pitchType || pitch.otherDetails?.meetingType || 'call',
    status:            pitch.status || 'active',
    overallPercentage: pitch.overallPercentage ?? pitch.score ?? 0,
    score:             pitch.score ?? 0,
    createdAt:         pitch.createdAt,
    duration:          pitch.duration ?? pitch.durationSeconds ?? pitch.callDurationSeconds ?? pitch.audioDurationSeconds,
    durationSeconds:   pitch.durationSeconds ?? pitch.duration ?? pitch.callDurationSeconds,
    durationMinutes:   pitch.durationMinutes ?? (pitch.duration ? Math.round(pitch.duration / 60) : undefined),
    ai_score:          pitch.overallScore ?? pitch.categoryScores?.scores ?? null,
    ai_summary:       pitch.ai_summary ?? {
      summary:     pitch.summary     ?? [],
      insights:    pitch.insights    ?? [],
      suggestions: pitch.suggestions ?? [],
    },
    feedback:          pitch.feedback ?? null,
    categoryScores:    pitch.categoryScores ?? null,
    summary:          pitch.summary     ?? [],
    insights:         pitch.insights    ?? [],
    suggestions:      pitch.suggestions ?? [],
    companyName:      pitch.TargetCompanyProfile?.companyName ?? pitch.companyName ?? pitch.customerData?.companyName ?? '',
    TargetCompanyProfile: pitch.TargetCompanyProfile ?? null,
    User:        pitch.User ?? null,
    user:        pitch.User ?? null,
    createdBy:   pitch.userId || pitch.user_id || pitch.user?.id || pitch.User?.id || pitch.createdBy,
    userId:      pitch.userId || pitch.user_id || pitch.user?.id || pitch.User?.id || pitch.createdBy,
    tenantId:    pitch.tenantId,
    pitchTitle:  pitch.pitchTitle,
    transcriptJson: pitch.transcriptJson ?? [],
    transcript_text: pitch.transcript || pitch.transcript_text || '',
    conversationAnalytics: pitch.conversationAnalytics ?? pitch.conversation_analytics ?? null,
    conversation_analytics: pitch.conversationAnalytics ?? pitch.conversation_analytics ?? null,
    talkRatio: pitch.talkRatio ?? { agent: 0, customer: 0 },
    otherDetails: {
      meetingType:      pitch.otherDetails?.meetingType ?? pitch.pitchType ?? 'call', 
      meetingPurpose:   pitch.otherDetails?.meetingPurpose   ?? [],
      meetingObjective: pitch.otherDetails?.meetingObjective ?? [],
      stageofSalesCycle: pitch.otherDetails?.stageofSalesCycle ?? pitch.stageofSalesCycle ?? [],
      dealMaturity:     pitch.otherDetails?.dealMaturity     ?? pitch.dealMaturity ?? '',
      buyerGoals:       pitch.otherDetails?.buyerGoals       ?? '',
      knownPainPoints:  pitch.otherDetails?.knownPainPoints  ?? '',
    },
    dealMaturity: pitch.otherDetails?.dealMaturity ?? pitch.dealMaturity ?? '',
    companyContact: pitch.companyContact ?? null,
    customerContact:   pitch.customerContact ?? null,
    customerData:      pitch.customerData ?? null,
  };
  return normalized as RawMeeting;
};

const normalizePracticeSession = (session: any): RawMeeting & { isScenario: boolean } => ({
  id:                session.id,
  meetingType:       'scenario',
  status:            session.status || 'completed',
  overallPercentage: session.overallScore ?? 0,
  score:             session.overallScore ?? 0,
  createdAt:         session.createdAt || session.sessionStart,
  duration:          session.duration ?? session.durationSeconds ?? (session.sessionEnd && session.sessionStart ? Math.round((new Date(session.sessionEnd).getTime() - new Date(session.sessionStart).getTime()) / 1000) : (session.scenario?.avgDurationMin ? session.scenario.avgDurationMin * 60 : undefined)),
  durationSeconds:   session.durationSeconds ?? session.duration,
  durationMinutes:   session.durationMinutes ?? session.scenario?.avgDurationMin,
  sessionStart:      session.sessionStart,
  sessionEnd:        session.sessionEnd,
  talkRatio:         undefined,
  ai_score:          session.aiFeedback?.metrics ?? null,
  ai_summary: {
    summary:     session.aiFeedback?.summary     ? [session.aiFeedback.summary] : [],
    insights:    session.aiFeedback?.strengths   ?? [],
    suggestions: session.aiFeedback?.improvements ?? [],
    coachingInsights: session.aiFeedback?.coachingInsights ?? null,
    objectionsDetected: session.aiFeedback?.objectionsDetected ?? null,
    buyingSignalsDetected: session.aiFeedback?.buyingSignalsDetected ?? null,
    missedOpportunities: session.aiFeedback?.missedOpportunities ?? null,
    dealIntelligence: session.aiFeedback?.dealIntelligence ?? null,
    objectionFeedback: session.aiFeedback?.objectionFeedback ?? null,
    buyingSignalInsight: session.aiFeedback?.buyingSignalInsight ?? null,
    missedOpportunityAdvice: session.aiFeedback?.missedOpportunityAdvice ?? null,
    liveAudioFeedback: session.aiFeedback?.liveAudioFeedback ?? null,
    energyLevel: session.aiFeedback?.energyLevel ?? null,
    interruptionsCount: session.aiFeedback?.interruptionsCount ?? null,
  },
  aiFeedback:       session.aiFeedback ?? null,
  summary:     session.aiFeedback?.summary     ? [session.aiFeedback.summary] : [],
  insights:    session.aiFeedback?.strengths   ?? [],
  suggestions: session.aiFeedback?.improvements ?? [],
  companyName: session.scenario?.title ?? '',
  TargetCompanyProfile: {
    id: session.scenario?.id || '',
    companyName: session.scenario?.title ?? '',
    industry: session.scenario?.industry ?? '',
    businessModel: session.scenario?.difficulty ?? '',
  },
  customerContact: {
    fullName: session.scenario?.personaTitle ?? '—',
  },
  companyContact: {
    jobTitle: session.scenario?.personaStyle ?? '',
    buyerPersona: session.scenario?.keyObjections ?? [],
  },
  User: session.user
    ? { id: session.user.id, name: session.user.name }
    : undefined,
  user: session.user
    ? { id: session.user.id, name: session.user.name }
    : undefined,
  userId:    session.userId || session.user_id || session.user?.id || session.createdBy,   
  createdBy: session.userId || session.user_id || session.user?.id || session.createdBy,
  tenantId:  session.tenantId,
  isScenario: true,
  conversationAnalytics: session.conversationAnalytics ?? session.conversation_analytics ?? null,
  conversation_analytics: session.conversationAnalytics ?? session.conversation_analytics ?? null,
  transcriptJson: session.transcriptJson ?? [],
  transcript_text: session.transcript || session.transcript_text || '',
  feedback:         session.feedback ?? null,
  otherDetails: {
    meetingType:      session.scenario?.meetingType ?? 'scenario',
    meetingPurpose:   [],
    meetingObjective: [],
  },
  // scenario ka title card mein dikhane ke liye
  pitchTitle: session.scenario?.title,
} as any);



export const usePitchDashboardData = (enabled: boolean) => {
const apiCalls = [
  createApiCall(
    'pitchOverview',
    () => getTenantDashboardOverview({ page: 1, pageSize: 1000, orderBy: 'desc' }),
    { enabled, staleTime: 0 },
  ),
];

  const { data, isLoading, isError, errors, refetchAll, refetchByKey } = useDashboardData(apiCalls);

  const rawPitches = useMemo<RawMeeting[]>(() => {
    // Handle different server payload shapes:
    // - { data: { pitches: { allPitches: [] }, practiceSessions: { sessions: [] } } }
    // - { pitches: [], practiceSessions: [] }
    // - { pitches: { items: [] }, practiceSessions: { items: [] } }
    const root = data?.pitchOverview?.data ?? data?.pitchOverview ?? {};
    const payload = root?.data ?? root;

    const pitches = Array.isArray(payload?.tenantPitchMeta?.items)
      ? payload.tenantPitchMeta.items
      : Array.isArray(payload?.pitches)
        ? payload.pitches
        : Array.isArray(payload?.pitches?.allPitches)
          ? payload.pitches.allPitches
          : Array.isArray(payload?.pitches?.items)
            ? payload.pitches.items
            : Array.isArray(payload?.allPitches)
              ? payload.allPitches
              : [];

    const sessions = Array.isArray(payload?.tenantScenarios)
      ? payload.tenantScenarios
      : Array.isArray(payload?.practiceSessions)
        ? payload.practiceSessions
        : Array.isArray(payload?.practiceSessions?.sessions)
          ? payload.practiceSessions.sessions
          : Array.isArray(payload?.practiceSessions?.items)
            ? payload.practiceSessions.items
            : Array.isArray(payload?.sessions)
              ? payload.sessions
              : [];

    const normalizedPitches = Array.isArray(pitches) ? pitches.map(normalizePitch) : [];
    const normalizedScenarios = Array.isArray(sessions)
      ? sessions.map(normalizePracticeSession)
      : [];

    return [...normalizedPitches, ...normalizedScenarios];
  }, [data?.pitchOverview]);

  

  return {
    rawPitches,
    isLoading,
    isError,
    errors,
    refetchAll,
    refetchByKey,
  };
};

